import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, CardActionArea,
  Skeleton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { apiClient } from '@/services/apiClient'
import { FamilySummary } from '@/types'

export default function FamilyList() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [family, setFamily] = useState<FamilySummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [newFamilyName, setNewFamilyName] = useState('')
  const [creating, setCreating] = useState(false)

  const loadFamily = async () => {
    try {
      const data = await apiClient.get<FamilySummary | null>('/api/families')
      setFamily(data)
      // If user has a family, redirect to its detail page
      if (data) {
        navigate(`/families/${data.id}`, { replace: true })
      }
    } catch {
      enqueueSnackbar('Failed to load family', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadFamily() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    if (!newFamilyName.trim()) return
    setCreating(true)
    try {
      const created = await apiClient.post<FamilySummary>('/api/families', { name: newFamilyName.trim() })
      setCreateOpen(false)
      setNewFamilyName('')
      enqueueSnackbar('Family created', { variant: 'success' })
      navigate(`/families/${created.id}`)
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to create family', { variant: 'error' })
    } finally {
      setCreating(false)
    }
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        My Family
      </Typography>

      {loading ? (
        <Skeleton variant="rounded" height={80} />
      ) : !family ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" gutterBottom>
              You haven't joined a family yet.
            </Typography>
            <Button variant="contained" onClick={() => setCreateOpen(true)}>
              Create Your Family
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card variant="outlined">
          <CardActionArea onClick={() => navigate(`/families/${family.id}`)}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{family.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {family.memberCount} member{family.memberCount !== 1 ? 's' : ''}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Create Family</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Family Name" value={newFamilyName}
            onChange={e => setNewFamilyName(e.target.value)} margin="dense"
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating || !newFamilyName.trim()}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
